import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCaretLeft, faCaretRight, faStepBackward, faStepForward, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasValue, styles } from "@repo/helpers";
import { useMemo } from "react";

import type { MarkupProps } from "./types";

import { DropDownList } from "../../dropDownList";
import style from "./style.module.scss";
import { SPACING } from "./types";

export default function PaginationMarkup(props: MarkupProps) {
	const { currentPage, pages, setPage, totalPages, totalItems, itemsPerPage, canSetItemsPerPage } = props;
	const startItem = useMemo(() => (currentPage - 1) * itemsPerPage + 1, [currentPage, itemsPerPage]);
	const endItem = useMemo(() => {
		const item = currentPage * itemsPerPage;
		if (item > totalItems) return totalItems;
		return item;
	}, [currentPage, totalItems, itemsPerPage]);

	const pageSizeSet = useMemo(() => {
		const set = new Set(props.pageSizes);
		set.add(itemsPerPage);
		return Array.from(set);
	}, [props.pageSizes, itemsPerPage]);
	const pageSizes = useMemo(
		() =>
			pageSizeSet
				.sort((a, b) => a - b)
				.map((num) => {
					return { value: num, label: `${num} items per page` };
				}),
		[pageSizeSet],
	);
	return <div className={style.pagination}>{getPaginationDisplay()}</div>;

	function getPaginationDisplay() {
		if (!hasValue(currentPage) || !hasValue(totalItems) || !hasValue(itemsPerPage)) return null;

		return (
			<>
				<div className={style.paginationGroup}>
					<PaginationButton
						icon={faStepBackward}
						label="First page"
						disabled={currentPage === 1}
						onClick={() => setPage(1)}
					/>
					<PaginationButton
						icon={faCaretLeft}
						label="Previous page"
						disabled={currentPage === 1}
						onClick={() => setPage(currentPage - 1)}
					/>
					{pages.map((page, index) => {
						if (page === -1)
							return (
								<span
									key={`ellipsis-${index}`}
									className={styles(style.paginationPage, style.ellipsis)}
									aria-hidden
								>
									{SPACING}
								</span>
							);
						return (
							<PaginationButton
								text={page}
								label={`Page ${page}`}
								key={page}
								onClick={() => setPage(page)}
								disabled={currentPage === page}
								active={currentPage === page}
							/>
						);
					})}
					<PaginationButton
						icon={faCaretRight}
						label="Next page"
						disabled={currentPage === totalPages}
						onClick={() => setPage(currentPage + 1)}
					/>
					<PaginationButton
						icon={faStepForward}
						label="Last page"
						disabled={currentPage === totalPages}
						onClick={() => setPage(totalPages)}
					/>
					<div className={style.totalCount}>
						{totalItems === 0 ? "No items to display" : `${startItem}-${endItem} of ${totalItems}`}
					</div>
				</div>
				<div className={style.paginationGroup}>
					{totalItems > 0 && canSetItemsPerPage && (
						<div className={style.totalCount} style={{ display: "inline-flex", alignItems: "center" }}>
							<DropDownList
								id="pageSizeSelector"
								values={pageSizes}
								value={itemsPerPage}
								inline
								disabled={totalItems < 5}
								changeHandler={(v) => props.setItemsPerPage(v ?? props.itemsPerPage)}
								label=""
							/>
						</div>
					)}
					{props.refresh && (
						<button type="button" className={style.refresh} onClick={props.refresh} aria-label="Refresh">
							<FontAwesomeIcon icon={faSync} />
						</button>
					)}
				</div>
			</>
		);
	}
}

interface PaginationButtonProps {
	icon?: IconProp;
	text?: number;
	label: string;
	key?: React.Key;
	disabled?: boolean;
	active?: boolean;
	onClick: () => void;
}
function PaginationButton({ icon, text, label, disabled, active, onClick }: PaginationButtonProps) {
	return (
		<button
			type="button"
			aria-label={label}
			aria-current={active ? "page" : undefined}
			disabled={disabled}
			onClick={onClick}
			className={styles(
				style.paginationPage,
				text !== undefined && style.pageNumber,
				disabled && style.disabled,
				active && style.active,
			)}
		>
			{icon && <FontAwesomeIcon icon={icon} />}
			{text}
		</button>
	);
}
